import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, FileText, Download } from "lucide-react";

const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const openProformaPdf = async (id) => {
  try {
    const resp = await api.get(`/proformas/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (e) { toast.error(formatApiError(e)); }
};

// Proforma = yalnız belge/PDF; stok/sevk/tahsilat/muhasebe/BH ve durum etkisi yoktur.
export default function ProformaDialog({ order, open, onOpenChange }) {
  const [validUntil, setValidUntil] = useState(plusDays(15));
  const [bankInfo, setBankInfo] = useState("");
  const [description, setDescription] = useState("");
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    setValidUntil(plusDays(15)); setBankInfo(""); setDescription("");
    api.get(`/orders/${order.id}/proformas`).then(({ data }) => setList(data)).catch(() => setList([]));
  }, [open, order]);

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/proformas`, {
        valid_until: validUntil, bank_info: bankInfo.trim() || null, description: description.trim(),
      });
      toast.success(`Proforma oluşturuldu: ${data.proforma_no}`);
      setList((l) => [data, ...l]);
      openProformaPdf(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-lg" data-testid="proforma-dialog">
        <DialogHeader><DialogTitle>Proforma Fatura — {order?.order_no}</DialogTitle></DialogHeader>
        <div className="text-[11px] text-slate-400 border border-slate-800 bg-slate-900/50 rounded-sm px-3 py-2">
          Proforma yalnızca belge oluşturur; stok, sevkiyat, tahsilat, muhasebe, Bizim Hesap ve kayıt durumu değişmez.
        </div>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase font-mono text-slate-500">Geçerlilik Tarihi</Label>
            <Input type="date" data-testid="proforma-valid-until" value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)} className="bg-slate-900 border-slate-800 rounded-sm h-9 w-44" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase font-mono text-slate-500">Banka Bilgileri</Label>
            <Textarea rows={3} data-testid="proforma-bank-info" value={bankInfo} onChange={(e) => setBankInfo(e.target.value)}
              placeholder="Boş bırakılırsa belge şablonundaki IBAN kullanılır"
              className="bg-slate-900 border-slate-800 rounded-sm resize-none text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase font-mono text-slate-500">Açıklama</Label>
            <Textarea rows={2} data-testid="proforma-description" value={description} onChange={(e) => setDescription(e.target.value)}
              className="bg-slate-900 border-slate-800 rounded-sm resize-none text-sm" />
          </div>
          <Button data-testid="proforma-create-submit" onClick={create} disabled={saving}
            className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 text-xs font-semibold uppercase tracking-wider">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1.5" />Proforma Oluştur ve PDF Aç</>}
          </Button>
          {list.length > 0 && (
            <div className="border-t border-slate-800 pt-3 space-y-1.5" data-testid="proforma-list">
              <div className="text-[10px] uppercase font-mono text-slate-500">Oluşturulan Proformalar</div>
              {list.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs" data-testid={`proforma-row-${p.proforma_no}`}>
                  <span className="font-mono text-slate-300">{p.proforma_no} · {(p.date || "").slice(0, 10)}</span>
                  <Button size="sm" variant="ghost" data-testid={`proforma-pdf-${p.proforma_no}`} onClick={() => openProformaPdf(p.id)}
                    className="h-7 text-blue-400 hover:bg-slate-800"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
